import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import '../../business/Business.css';
import '../payments/PaymentProviderDetailsPage.css';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import DeleteProductModal from '../../dashboard/components/product_item_components/DeleteProductModal';
import { TrashIcon } from '../../dashboard/components/product_item_components/icons';
import {
  EKASSA_TAXATION_SYSTEMS,
  ReceiptProviderTypes,
} from '../../../types/ReceiptProviderTypes';

const parseDigit = (value, min, max) => {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const number = Number(text);
  if (number < min || number > max) return null;
  return number;
};

function ReceiptProviderDetailsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [providerId, setProviderId] = React.useState(null);
  const [inn, setInn] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [fiscalNumber, setFiscalNumber] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');
  const [st, setSt] = React.useState('');
  const [vat, setVat] = React.useState('');
  const [taxationSystemId, setTaxationSystemId] = React.useState('');
  const [loadingDetails, setLoadingDetails] = React.useState(true);
  const [submitError, setSubmitError] = React.useState(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const listPath = `/branch/${branchId}/receipt-providers`;

  React.useEffect(() => {
    if (!branchId || branchId === 'store') {
      setLoadingDetails(false);
      setSubmitError('Выберите филиал');
      return;
    }

    let cancelled = false;
    setLoadingDetails(true);
    setSubmitError(null);
    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/branches/${encodeURIComponent(branchId)}/receipt-providers`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить кассу');
          return;
        }
        const list = Array.isArray(res?.data?.receiptProviders) ? res.data.receiptProviders : [];
        const existing = list.find((row) => Number(row.typeId) === ReceiptProviderTypes.EKASSA);
        if (!existing) return;
        setProviderId(existing.id ?? null);
        setInn(String(existing.inn ?? ''));
        setCompanyName(String(existing.companyName ?? ''));
        setAddress(String(existing.address ?? ''));
        setFiscalNumber(String(existing.credentials?.fiscalNumber ?? ''));
        setSt(existing.credentials?.st == null ? '' : String(existing.credentials.st));
        setVat(existing.credentials?.vat == null ? '' : String(existing.credentials.vat));
        setTaxationSystemId(
          existing.credentials?.taxationSystemId == null
            ? ''
            : String(existing.credentials.taxationSystemId)
        );
        setSecretKey('');
      } catch (_) {
        if (!cancelled) setSubmitError('Ошибка загрузки');
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, requestWithMeta]);

  const isEdit = providerId != null;
  const secretRequired = !isEdit;
  const stValue = parseDigit(st, 0, 100);
  const vatValue = parseDigit(vat, 0, 100);
  const taxValue = parseDigit(taxationSystemId, 0, 14);
  const saveDisabled =
    loading ||
    loadingDetails ||
    deleting ||
    !inn.trim() ||
    !companyName.trim() ||
    !address.trim() ||
    !fiscalNumber.trim() ||
    (secretRequired && !secretKey.trim()) ||
    stValue == null ||
    vatValue == null ||
    taxValue == null;

  const handleSave = async () => {
    setSubmitError(null);
    const credentials = {
      fiscalNumber: fiscalNumber.trim(),
      st: stValue,
      vat: vatValue,
      taxationSystemId: taxValue,
    };
    if (secretKey.trim()) credentials.secretKey = secretKey.trim();

    const body = {
      typeId: ReceiptProviderTypes.EKASSA,
      inn: inn.trim(),
      companyName: companyName.trim(),
      address: address.trim(),
      credentials,
    };

    if (isEdit) {
      const res = await requestWithMeta(
        `/operator/receipt-providers/${encodeURIComponent(providerId)}`,
        'PUT',
        body
      );
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить кассу');
        return;
      }
      setSecretKey('');
      navigate(listPath);
      return;
    }

    const res = await requestWithMeta(
      `/operator/branches/${encodeURIComponent(branchId)}/receipt-providers`,
      'POST',
      body
    );
    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить кассу');
      return;
    }
    navigate(listPath);
  };

  const handleConfirmDelete = async () => {
    if (providerId == null) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      const res = await requestWithMeta(
        `/operator/receipt-providers/${encodeURIComponent(providerId)}`,
        'DELETE'
      );
      if (res?.ok) {
        setConfirmOpen(false);
        navigate(listPath);
        return;
      }
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить кассу');
    } catch (_) {
      setDeleteError('Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="shop-create-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="eKassa"
          subtitle={isEdit ? 'Измените данные кассы' : 'Укажите данные кассы'}
          onBack={() => navigate(listPath)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-inn">
              ИНН <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-inn"
              className="shop-create-input"
              value={inn}
              onChange={(e) => setInn(e.target.value)}
              autoComplete="off"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-company">
              Название компании <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-company"
              className="shop-create-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              autoComplete="organization"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-address">
              Адрес <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-address"
              className="shop-create-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="street-address"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-fiscal">
              Фискальный номер <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-fiscal"
              className="shop-create-input"
              value={fiscalNumber}
              onChange={(e) => setFiscalNumber(e.target.value)}
              autoComplete="off"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-secret">
              Секретный ключ {secretRequired ? <b style={{ color: 'red' }}>*</b> : null}
            </label>
            <input
              id="ekassa-secret"
              className="shop-create-input"
              type="password"
              value={secretKey}
              placeholder={isEdit ? 'Оставьте пустым, чтобы не менять' : ''}
              onChange={(e) => setSecretKey(e.target.value)}
              autoComplete="new-password"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-st">
              НСП, % <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-st"
              className="shop-create-input"
              inputMode="numeric"
              value={st}
              onChange={(e) => setSt(e.target.value)}
              autoComplete="off"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-vat">
              НДС, % <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="ekassa-vat"
              className="shop-create-input"
              inputMode="numeric"
              value={vat}
              onChange={(e) => setVat(e.target.value)}
              autoComplete="off"
              disabled={loadingDetails}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="ekassa-tax">
              Система налогообложения <b style={{ color: 'red' }}>*</b>
            </label>
            <select
              id="ekassa-tax"
              className="shop-create-input"
              value={taxationSystemId}
              onChange={(e) => setTaxationSystemId(e.target.value)}
              disabled={loadingDetails}
            >
              <option value="">Выберите</option>
              {EKASSA_TAXATION_SYSTEMS.map((system) => (
                <option key={system.id} value={String(system.id)}>
                  {system.name}
                </option>
              ))}
            </select>
          </section>
        </main>

        <CreateFooter
          disabled={saveDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить'}
          onCreate={handleSave}
          leading={isEdit ? (
            <button
              type="button"
              className="payment-provider-deleteButton"
              aria-label="Удалить eKassa"
              title="Удалить"
              disabled={loading || deleting || loadingDetails}
              onClick={() => {
                setDeleteError(null);
                setConfirmOpen(true);
              }}
            >
              <TrashIcon />
            </button>
          ) : null}
        />
      </div>

      <DeleteProductModal
        isOpen={confirmOpen}
        onClose={() => {
          if (deleting) return;
          setConfirmOpen(false);
          setDeleteError(null);
        }}
        title="Удалить кассу"
        productName="eKassa"
        deleteError={deleteError}
        loading={deleting}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default ReceiptProviderDetailsPage;
